/**
 * i18n for the in-page review panel and the friendly error messages in
 * index.ts. The widget is silent for ordinary visitors — only the review
 * surface (reached via a signed ?hbReviewPage deep link) shows text, so only
 * that needs localization. barStrings went with the legacy single-patch
 * review bar in #199.
 *
 * Locale resolves from HelpBobConfig.locale → the customer page's <html lang> →
 * 'en'. English is the safe default. Add a language by extending each builder.
 */

export type WidgetLocale = 'en' | 'de';

/** Resolve the widget locale: explicit config → <html lang> → English. */
export function resolveWidgetLocale(configLocale?: string): WidgetLocale {
  const norm = (v: string | null | undefined): string => (v || '').toLowerCase().slice(0, 2);
  const c = norm(configLocale);
  if (c === 'de') return 'de';
  if (c === 'en') return 'en';
  const html = norm(typeof document !== 'undefined' ? document.documentElement.lang : '');
  return html === 'de' ? 'de' : 'en';
}

/** Localized strings for the review BAR (single-patch review). */
/** Localized strings for the review PANEL (per-page list of changes). */
export function panelStrings(locale: WidgetLocale) {
  const de = locale === 'de';
  return {
    title: de ? 'HelpBob-Überprüfung' : 'HelpBob review',
    unavailable: de ? 'Überprüfung nicht verfügbar' : 'Review unavailable',
    empty: de ? 'Nichts in dieser Ansicht.' : 'Nothing in this view.',
    collapse: de ? 'Zu Pille einklappen' : 'Collapse to pill',
    close: de ? 'Schließen' : 'Close',
    closeAria: de ? 'Überprüfungs-Panel schließen' : 'Close review panel',
    dismiss: de ? 'Verwerfen' : 'Dismiss',
    pending: de ? 'offen' : 'pending',
    changes: (n: number) => (de ? `${n} Änderung${n === 1 ? '' : 'en'}` : `${n} change${n === 1 ? '' : 's'}`),
    expiresTitle: (m: number) => (de
      ? `Link läuft in ${m} Min ab — drücke ? für Tastaturkürzel`
      : `Link expires in ${m} min — press ? for keyboard shortcuts`),
    showShortcuts: de ? 'Tastaturkürzel anzeigen' : 'Show keyboard shortcuts',
    filterPending: de ? 'Offen' : 'Pending',
    filterActive: de ? 'Aktiv' : 'Active',
    filterRejected: de ? 'Abgelehnt' : 'Rejected',
    filterAll: de ? 'Alle' : 'All',
    statePending: de ? 'Offen' : 'Pending review',
    stateActive: de ? 'Aktiv' : 'Active',
    stateRejected: de ? 'Abgelehnt' : 'Rejected',
    stateDisabled: de ? 'Deaktiviert' : 'Disabled',
    reasonWrongFix: de ? 'Falscher Fix' : 'Wrong fix',
    reasonNotNeeded: de ? 'Nicht nötig' : 'Not needed',
    reasonLater: de ? 'Später erledigen' : 'Will do later',
    previewing: de ? 'Vorschau aktiv' : 'Previewing',
    preview: de ? 'Vorschau' : 'Preview',
    previewedOff: de ? 'Vorschau aus' : 'Previewed off',
    previewOff: de ? 'Vorschau aus' : 'Preview off',
    accept: de ? 'Freigeben' : 'Accept',
    reject: de ? 'Ablehnen' : 'Reject',
    disable: de ? 'Deaktivieren' : 'Disable',
    reinstate: de ? 'Wiederherstellen' : 'Reinstate',
    reEnable: de ? 'Wieder aktivieren' : 'Re-enable',
    whyChange: de ? 'Warum diese Änderung' : 'Why this change',
    codeDiff: de ? 'Code-Diff' : 'Code diff',
    noWhy: de ? 'Keine weitere Erklärung für diese Änderung hinterlegt.' : 'No further explanation recorded for this change.',
    customReason: de ? 'Oder eigener Grund… (Enter zum Ablehnen)' : 'Or custom reason… (Enter to reject)',
    otherPages: de ? 'Andere Seiten mit offenen Änderungen' : 'Other pages with pending changes',
    pendingHere: (n: number) => (de ? `${n} offen auf dieser Seite` : `${n} pending on this page`),
    previewAll: de ? 'Alle ansehen' : 'Preview all',
    acceptAll: de ? 'Alle freigeben' : 'Accept all',
    rejectAll: de ? 'Alle ablehnen' : 'Reject all',
    confirm: de ? 'Bestätigen?' : 'Confirm?',
    shortcutsTitle: de ? 'Tastaturkürzel' : 'Keyboard shortcuts',
    closeShortcuts: de ? 'Tastaturkürzel schließen' : 'Close shortcuts',
    scPreview: de ? 'Vorschau auf fokussierter Karte umschalten' : 'Toggle preview on focused card',
    scAccept: de ? 'Fokussierte Karte freigeben' : 'Accept focused card',
    scReject: de ? 'Fokussierte Karte ablehnen' : 'Reject focused card',
    scNav: de ? 'Nächste / vorherige Karte' : 'Next / prev card',
    scCollapse: de ? 'Panel einklappen' : 'Collapse panel',
    saveError: de ? 'Deine Entscheidung konnte nicht gespeichert werden. Bitte erneut versuchen.' : 'Could not save your decision. Try again.',
    // Shown on the dashed frame when the patched element is hidden and we can
    // only bracket its nearest visible ancestor — see applyOutline in index.ts.
    approxArea: de ? 'ungefährer Bereich – Element ausgeblendet' : 'approximate area – element hidden',
    // Compact marker on the review card for the same situation.
    hiddenAreaTag: de ? 'versteckt · ungefährer Bereich' : 'hidden · approximate area',
    hiddenAreaHint: de
      ? 'Das Element ist ausgeblendet (z. B. display:none). Der Rahmen markiert nur den ungefähren Bereich (nächster sichtbarer Vorfahr).'
      : 'The element is hidden (e.g. display:none). The frame marks only its approximate area (nearest visible ancestor).',
    // Hidden element with NO visible ancestor to bracket (collapsed menu / closed
    // modal): we can't frame it, so we name the region it lives in. The region
    // name (e.g. "#mobile-menu") is appended raw at render time.
    hiddenNoAreaTag: de ? 'versteckt · im ausgeblendeten Bereich' : 'hidden · in collapsed region',
    hiddenNoAreaHint: de
      ? 'Das Element liegt in einem aktuell ausgeblendeten Bereich (z. B. zugeklapptes Menü oder geschlossener Dialog) und kann nicht eingerahmt werden.'
      : 'The element sits in a currently collapsed region (e.g. a closed menu or dialog) and cannot be framed.',
  };
}

/** Localized friendly-error messages used in index.ts. */
export function errorStrings(locale: WidgetLocale) {
  const de = locale === 'de';
  return {
    linkExpired: de
      ? 'Dieser Überprüfungslink ist abgelaufen. Öffne ihn erneut aus deinem HelpBob-Dashboard.'
      : 'This review link has expired. Open it again from your HelpBob dashboard.',
    patchGone: de
      ? 'Dieser Fix ist nicht mehr verfügbar — er wurde möglicherweise deaktiviert oder abgelehnt.'
      : 'This fix is no longer available — it may have been disabled or rejected.',
    patchGoneShort: de ? 'Dieser Fix ist nicht mehr verfügbar.' : 'This fix is no longer available.',
    linkInvalid: de
      ? 'Dieser Überprüfungslink ist für diese Website nicht gültig.'
      : 'This review link is not valid for this site.',
    applyFailed: de
      ? 'Dieser Fix konnte auf der aktuellen Seite nicht angewendet werden. Das Element wurde evtl. verschoben oder entfernt.'
      : "Couldn't apply this fix on the current page. The element may have moved or been removed.",
    saveError: de ? 'Deine Entscheidung konnte nicht gespeichert werden. Bitte erneut versuchen.' : 'Could not save your decision. Try again.',
    serverUnreachable: de
      ? 'Der HelpBob-Server ist nicht erreichbar. Prüfe deine Verbindung und versuche es erneut.'
      : "Couldn't reach the HelpBob server. Check your connection and try again.",
    // #87 hardening codes. deviceMismatch MUST name the recovery (re-mint on
    // THIS device) — the generic linkInvalid would misdiagnose it as a broken
    // install and dead-end the reviewer.
    tokenConsumed: de
      ? 'Über diesen Link wurde bereits entschieden. Für eine neue Überprüfung öffne den Fix erneut aus deinem HelpBob-Dashboard.'
      : 'This link was already used to decide. To review again, reopen the fix from your HelpBob dashboard.',
    deviceMismatch: de
      ? 'Dieser Überprüfungslink ist an das Gerät gebunden, auf dem er zuerst geöffnet wurde. Öffne dein HelpBob-Dashboard auf DIESEM Gerät und klicke erneut auf „Auf Seite prüfen".'
      : 'This review link is locked to the device where it was first opened. Open your HelpBob dashboard on THIS device and click "Review on page" again.',
    reviewTempUnavailable: de
      ? 'Die Überprüfung ist gerade nicht verfügbar. Bitte lade die Seite in ein paar Sekunden neu.'
      : 'Review is temporarily unavailable. Please reload the page in a few seconds.',
  };
}

/**
 * Canonical reject-reason chips (stable code + localized label) shared by the
 * review BAR and PANEL. The `code` values are locale-independent and kept in
 * sync with @helpbob/shared REJECT_REASON_CODES; the free-text box covers the
 * fifth code, 'other'. Splitting "not a real problem" from "the fix broke my
 * page" is deliberate — the quality loop must not blame detection for a bad fix.
 */
export function rejectReasonChips(locale: WidgetLocale): Array<{ code: string; label: string }> {
  const de = locale === 'de';
  return [
    { code: 'false_positive', label: de ? 'Kein echtes Problem' : 'Not a real problem' },
    { code: 'broke_site', label: de ? 'Fix hat die Seite beschädigt' : 'The fix broke my page' },
    { code: 'already_accessible', label: de ? 'War schon barrierefrei' : 'Already accessible' },
    { code: 'not_important', label: de ? 'Für uns nicht wichtig' : 'Not important to us' },
  ];
}

/**
 * Serialize a reject reason for `patch_audit_log.reason`: a bare canonical code,
 * or `"other: <free text>"` for a custom reason. Mirrors @helpbob/shared
 * formatRejectReason so both surfaces store the identical string shape.
 */
export function formatReviewReason(code: string, text?: string): string {
  const t = (text || '').trim();
  if (code === 'other') return t ? `other: ${t}` : 'other';
  return t ? `${code}: ${t}` : code;
}
